/**
 * "Saved here, but Backoffice isn't updated yet" — the persisted marker.
 *
 * The Hub saves an edit first and then pushes it to the Backoffice. When that
 * push fails, the row keeps a marker until a later push succeeds, so the state
 * survives a reload. This shows it as a calm badge: the edit is not lost, the
 * two systems just disagree for now. The reason is for the team, so it sits in
 * the tooltip rather than on the page. Admin-only data; renders nothing when
 * there is no marker.
 */

import { Badge, Text, Tooltip } from '@mantine/core';
import { IconCloudOff } from '@tabler/icons-react';

import type { BackofficeSyncMarker } from '@/types/backoffice-sync';

interface BackofficeBehindNoticeProps {
  marker: BackofficeSyncMarker | null | undefined;
  /** What is behind, in a few words — "price and due date", "partner fee". */
  what?: string;
}

function formatFailedAt(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

export function BackofficeBehindNotice({ marker, what }: Readonly<BackofficeBehindNoticeProps>) {
  if (!marker?.error) {
    return null;
  }
  const when = formatFailedAt(marker.failedAt);
  return (
    <Tooltip
      multiline
      w={300}
      withArrow
      label={
        <>
          <Text size="xs" fw={600}>
            Saved in GTI Hub. Backoffice
            {what ? ` (${what})` : ''} was not updated.
          </Text>
          <Text size="xs">{marker.error}</Text>
          {when && (
            <Text size="xs" mt={4}>
              Failed {when}.
            </Text>
          )}
          <Text size="xs" mt={4}>
            This clears the next time a change reaches Backoffice.
          </Text>
        </>
      }
    >
      <Badge
        color="orange"
        variant="light"
        size="sm"
        leftSection={<IconCloudOff size={11} />}
        data-testid="backoffice-behind-notice"
        tabIndex={0}
      >
        Backoffice not updated yet
      </Badge>
    </Tooltip>
  );
}
